import React, { useCallback, useEffect, useMemo, useState } from "react";
import useAssistance from "../hooks/useAssistance";
import useChimeMeeting from "../hooks/useChimeMeeting.js";
import moment from "moment";
import { useAlert, useView } from "../hooks";
import { MessageSenderType, PluginView } from "../enums";
import { useTranslation } from "react-i18next";

export const ChatContext = React.createContext({});

/**
 * The chat context provider
 * 
 * @param {Object} props The props of the component
 * @returns The chat context provider
 */
const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [readMarker, setReadMarker] = useState(0);
  const { assistance } = useAssistance();
  const {
    addMessagingObserver,
    hasJoined,
    removeMessagingObserver,
    listStandardMessages,
    sendStandardMessage,
    getReadMarker,
    updateReadMarker,
  } = useChimeMeeting();
  const { pluginView, isChatOpen } = useView();
  const { alert } = useAlert();
  const { t } = useTranslation();

  /**
   * Handles the standard message created
   * 
   * @param {Object} message The message object
   */
  const onStandardMessageCreated = useCallback((message) => {
    setMessages((prevMessages) => [...prevMessages, message]);
  }, []);

  /**
   * Handles the standard message updated
   * 
   * @param {Object} message The message object
   */
  const onStandardMessageUpdated = useCallback((message) => {
    let updated = false;

    setMessages((prevMessages) =>
      prevMessages.map((m) => {
        if (m.MessageId === message.MessageId) {
          updated = true;
          return message;
        }
        return m;
      }),
    );
    if (!updated) {
      setMessages((prevMessages) => [...prevMessages, message]);
    }
  }, []);

  const unreadMessagesLength = useMemo(() => {
    const readMarkerDate = new Date(readMarker);

    return messages?.filter((message) => {
      return (
        message.Sender?.Name !== MessageSenderType.OPERATOR &&
        (!readMarker || new Date(message.LastUpdatedTimestamp) > readMarkerDate)
      );
    }).length;
  }, [messages, readMarker]);

  const messagingObserver = useMemo(
    () => ({
      messagingSessionDidStartConnecting(reconnecting) {
        console.log("Messaging session did start connecting", reconnecting);
      },
      messagingSessionDidStart() {
        console.log("Messaging session did start");
      },
      messagingSessionDidStop(event) {
        console.log("Messaging session did stop", event);
      },
      messagingSessionDidReceiveMessage(message) {
        const { headers, payload, type } = message;
        const parsedPayload = JSON.parse(payload);

        console.log(
          "Messaging session did receive message",
          headers,
          payload,
          type,
        );

        if (headers?.["x-amz-chime-message-type"] === "STANDARD") {
          if (type === "CREATE_CHANNEL_MESSAGE") {
            onStandardMessageUpdated(parsedPayload);
          } else if (type === "PENDING_CREATE_CHANNEL_MESSAGE") {
            onStandardMessageCreated(parsedPayload);
          } else if (type === "FAILED_CREATE_CHANNEL_MESSAGE") {
            onStandardMessageUpdated(parsedPayload);
          } else if (type === "DENIED_CREATE_CHANNEL_MESSAGE") {
            onStandardMessageUpdated(parsedPayload);
          }
        } else if (headers?.["x-amz-chime-message-type"] === "CONTROL") {
          if (type === "CREATE_CHANNEL_MESSAGE") {
            const jsonContent = JSON.parse(parsedPayload?.Content)

            if (jsonContent?.muteVideo === true) {
              alert({
                id: "user-mute-video",
                title: t("plugin.toasts.driverMuteVideo.title"),
                status: "warning",
              });
            }
            else if (jsonContent?.muteVideo === false) {
              alert({
                id: "user-unmute-video",
                title: t("plugin.toasts.driverUnmuteVideo.title"),
                status: "info",
              });
            }
            if (jsonContent?.muteAudio === true) {
              alert({
                id: "user-mute-audio",
                title: t("plugin.toasts.driverMuteAudio.title"),
                status: "warning",
              });
            }
            else if (jsonContent?.muteAudio === false) {
              alert({
                id: "user-unmute-audio",
                title: t("plugin.toasts.driverUnmuteAudio.title"),
                status: "info",
              });
            }
          }
        }
      },
    }),
    [onStandardMessageCreated, onStandardMessageUpdated, alert, t],
  );

  useEffect(() => {
    if (hasJoined) {
      addMessagingObserver(messagingObserver);
      return () => {
        removeMessagingObserver(messagingObserver);
      };
    }
  }, [
    addMessagingObserver,
    hasJoined,
    messagingObserver,
    removeMessagingObserver,
  ]);

  /**
   * Handles the message send
   * 
   * @param {Object} message The message object
   */
  const onMessageSend = useCallback(
    async (message) => {
      await sendStandardMessage(message);
    },
    [sendStandardMessage],
  );

  /**
   * Handles the chat export
   */
  const onChatExport = useCallback(() => {
    if (messages.length > 0) {
      const fileTitle = `${assistance.license_plate} - ${moment().unix()}.txt`;
      const fileContent = new Blob(
        [
          messages
            .map((message) => {
              const date = moment(message.CreatedTimestamp);

              return `${date.format("L")} ${date.format("LT")} (${message.Sender?.Name
                }) => ${message.Content}`;
            })
            .join("\n"),
        ],
        { type: "text/plain" },
      );
      const downloadLink = document.createElement("a");

      downloadLink.href = window.URL.createObjectURL(fileContent);
      downloadLink.download = fileTitle;
      downloadLink.click();
    }
  }, [assistance, messages]);

  useEffect(() => {
    const flushReadMarker = async () => {
      await updateReadMarker();
      const readMarkerTimestamp = await getReadMarker();

      setReadMarker(readMarkerTimestamp);
    };
    if (
      unreadMessagesLength > 0 &&
      (isChatOpen || pluginView === PluginView.FULL)
    ) {
      flushReadMarker();
    }
  }, [
    pluginView,
    isChatOpen,
    getReadMarker,
    updateReadMarker,
    unreadMessagesLength,
  ]);

  useEffect(() => {
    (async () => {
      const messageList = await listStandardMessages();

      setMessages(messageList);
    })();
  }, [listStandardMessages]);

  useEffect(() => {
    (async () => {
      const readMarkerTimestamp = await getReadMarker();

      setReadMarker(readMarkerTimestamp);
    })();
  }, [getReadMarker]);

  const contextValue = useMemo(
    () => ({
      messages,
      onChatExport,
      onMessageSend,
      unreadMessagesLength,
    }),
    [messages, onChatExport, onMessageSend, unreadMessagesLength],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};

export default React.memo(ChatProvider);
